import React from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  User, 
  Building2, 
  Wrench, 
  CreditCard,
  ClipboardCheck,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';

// Awaiting with icons and colors
const AWAITING_CONFIG = {
  'Internal Ops': { icon: ClipboardCheck, color: 'bg-teal-500', textColor: 'text-teal-700', bgColor: 'bg-teal-50' },
  'Contractor': { icon: Wrench, color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50' },
  'Client': { icon: Building2, color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
  'Resident': { icon: User, color: 'bg-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50' },
  'Finance': { icon: CreditCard, color: 'bg-purple-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50' },
  'Surveyor': { icon: ClipboardCheck, color: 'bg-cyan-500', textColor: 'text-cyan-700', bgColor: 'bg-cyan-50' },
  'None': { icon: CheckCircle, color: 'bg-slate-400', textColor: 'text-slate-600', bgColor: 'bg-slate-50' }
};

// Compute workflow status from job data
export function computeWorkflowStatus(job) {
  if (!job) return { awaiting_with: 'Unknown', next_required_action: 'Load job data' };
  
  const status = job.status || 'New';
  const hasContractor = !!(job.assigned_contractor_id || job.contractor_id || job.assigned_contractor);
  const hasQuote = !!(job.quote_value || job.quotes?.length > 0);
  const quoteStatus = job.quote_status || '';
  const invoiceStatus = job.invoice_status || '';
  
  const statusLower = status.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  
  // Default values
  let awaiting_with = 'Internal Ops';
  let next_required_action = 'Review job';
  
  // Status-based logic
  if (['new', 'enquiry', 'job_created'].includes(statusLower)) {
    if (!hasContractor) {
      awaiting_with = 'Internal Ops';
      next_required_action = 'Assign contractor';
    } else {
      awaiting_with = 'Contractor';
      next_required_action = 'Contractor to accept job';
    }
  } else if (['assigned', 'pending_acceptance'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Contractor to accept job';
  } else if (['awaiting_quote', 'quote_requested'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Contractor to submit quote';
  } else if (['quote_submitted', 'quote_received'].includes(statusLower)) {
    awaiting_with = 'Internal Ops';
    next_required_action = 'Internal quote review required';
  } else if (['quote_approved', 'quote_awaiting_approval'].includes(statusLower)) {
    if (job.requires_client_approval) {
      awaiting_with = 'Client';
      next_required_action = 'Client approval required';
    } else {
      awaiting_with = 'Internal Ops';
      next_required_action = 'Issue PO';
    }
  } else if (['po_issued', 'works_ordered'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Schedule works';
  } else if (['scheduled'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Complete works';
  } else if (['in_progress', 'works_in_progress'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Complete works and upload evidence';
  } else if (['completed', 'awaiting_review'].includes(statusLower)) {
    awaiting_with = 'Internal Ops';
    next_required_action = 'Review evidence and approve completion';
  } else if (['awaiting_invoice'].includes(statusLower)) {
    awaiting_with = 'Contractor';
    next_required_action = 'Contractor invoice required';
  } else if (['invoiced', 'invoice_submitted'].includes(statusLower)) {
    awaiting_with = 'Finance';
    next_required_action = 'Finance invoice review required';
  } else if (['approved', 'invoice_approved'].includes(statusLower)) {
    awaiting_with = 'Finance';
    next_required_action = 'Mark paid';
  } else if (['paid'].includes(statusLower)) {
    awaiting_with = 'Internal Ops';
    next_required_action = 'Close job';
  } else if (['closed'].includes(statusLower)) {
    awaiting_with = 'None';
    next_required_action = 'Job complete';
  } else if (['cancelled'].includes(statusLower)) {
    awaiting_with = 'None';
    next_required_action = 'Job cancelled';
  }
  
  return {
    awaiting_with,
    next_required_action,
    status,
    has_contractor: hasContractor,
    has_quote: hasQuote
  };
}

export default function WorkflowStatusBar({ job, className = "" }) {
  const workflowStatus = computeWorkflowStatus(job);
  const config = AWAITING_CONFIG[workflowStatus.awaiting_with] || AWAITING_CONFIG['Internal Ops'];
  const StatusIcon = config.icon;
  
  return (
    <div className={`flex items-center gap-4 p-3 bg-white rounded-xl border border-slate-200 ${className}`} data-testid="workflow-status-bar">
      {/* Awaiting With */}
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
          <StatusIcon className="h-4 w-4 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Awaiting</div>
          <div className={`text-sm font-semibold ${config.textColor}`}>
            {workflowStatus.awaiting_with}
          </div>
        </div>
      </div>
      
      {/* Arrow */}
      <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
      
      {/* Next Required Action */}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Next Action</div>
        <div className="text-sm font-medium text-slate-700 truncate">
          {workflowStatus.next_required_action}
        </div>
      </div>
    </div>
  );
}

// Compact inline version for tables/lists
export function WorkflowStatusInline({ job }) {
  const workflowStatus = computeWorkflowStatus(job);
  const config = AWAITING_CONFIG[workflowStatus.awaiting_with] || AWAITING_CONFIG['Internal Ops'];
  
  return (
    <div className="flex items-center gap-2" data-testid="workflow-status-inline">
      <Badge variant="outline" className={`text-[10px] ${config.bgColor} ${config.textColor} border-0`}>
        {workflowStatus.awaiting_with}
      </Badge>
      <span className="text-[10px] text-slate-400">→</span>
      <span className="text-[10px] text-slate-600 truncate max-w-[150px]">
        {workflowStatus.next_required_action}
      </span>
    </div>
  );
}
